const getViewport = () => ({
  height: window.innerHeight || document.documentElement.offsetHeight,
  width: window.innerWidth || document.documentElement.offsetWidth,
})

const scrollToTop = el => {
  if (el) {
    el.scrollTop = 0
  }
}

/*
 * Opening the on-screen keyboard for a field that is not on screen yet.
 *
 * iOS Safari only raises the keyboard for a focus() that runs inside the tap that asked for it, and
 * a field on the screen being navigated to has not been mounted by then. An autofocus on arrival
 * therefore gives that field the cursor but leaves the keyboard shut.
 *
 * So the tap focuses a stand-in field instead, which raises the keyboard there and then. Appending
 * it straight to the document rather than rendering it is the point: a route change cannot unmount
 * it, so the keyboard has something to stay open for until the real field mounts and takes the
 * focus off it. Moving focus between two text fields does not need a gesture of its own, so the
 * keyboard stays up across the handover.
 */
let keyboardPrimer = null

const removeKeyboardPrimer = () => {
  if (!keyboardPrimer) return

  const input = keyboardPrimer
  // Cleared first: removing the node fires another blur, which would land back in here.
  keyboardPrimer = null
  input.remove()
}

/**
 * Raises the on-screen keyboard, to be kept up by whatever field is focused next.
 *
 * Call this synchronously inside the tap that leads to the field — a focus() any later than that
 * is too late for iOS. Only worth doing on a touch viewport; elsewhere an autofocus is enough.
 */
const primeKeyboard = () => {
  if (keyboardPrimer) return

  const input = document.createElement('input')
  input.type = 'text'
  input.tabIndex = -1
  input.setAttribute('aria-hidden', 'true')
  /* Transparent and out of the way, but laid out and focusable: a field iOS cannot render is one it
     will not open the keyboard for, which rules out display:none and visibility:hidden. The font
     size is spelled out because anything under 16px makes iOS zoom the page in on focus. */
  input.style.cssText =
    'position:fixed;top:0;left:0;width:1px;height:1px;padding:0;border:0;opacity:0;font-size:16px;z-index:-1'
  // Cleans itself up the moment it loses focus, so nothing is left behind if the field never comes.
  input.addEventListener('blur', removeKeyboardPrimer)

  document.body.appendChild(input)
  input.focus()
  keyboardPrimer = input
}

/**
 * Drops the stand-in field. Focus the real field first: dropping it while it still holds the focus
 * is what closes the keyboard, which is the whole thing this is here to avoid.
 */
const releaseKeyboardPrimer = removeKeyboardPrimer

export { getViewport, primeKeyboard, releaseKeyboardPrimer, scrollToTop }
